import type { Creator, Item, List } from "../lib/api.js";
import type { ItemChange, ListChange, SharedStore, ShareState, Status, SyncState } from "./store.js";

/**
 * One shared page's store, whose scope can change under a screen that stays
 * mounted (g72).
 *
 * A shared scope names its visitor (`scopes.ts`), so the moment an unnamed
 * editor gives a name, or a background check finds someone else is looking,
 * the page's copy is another database. Rebuilding the screen on it would
 * throw away everything on screen that is not in the store: the text half
 * typed in a row, the Details form, the Add popup, what is expanded, the
 * scroll and the focus. So the screen holds this instead, and this holds
 * the scope's store. Its watchers stay registered here; on a switch the new
 * store's rows arrive as a remote delta would, row by row, and only where
 * they differ.
 */
export interface SwitchableStore extends SharedStore {
  /** The store this forwards to now. */
  current(): SharedStore;
  /**
   * Hands everything over to `next`, which the caller has opened and synced,
   * and closes the store it replaces. Never deletes that one: another tab
   * may be showing it, and what it holds unsent is still its visitor's.
   */
  switchTo(next: SharedStore): Promise<void>;
}

export function switchableStore(initial: SharedStore): SwitchableStore {
  let current = initial;
  /** Counted per list, as the store counts its own leases. */
  const leases = new Map<string, { count: number; release: () => void }>();

  const lists = new Set<(change: ListChange) => void>();
  const items = new Map<string, Set<(change: ItemChange) => void>>();
  const allItems = new Set<(change: ItemChange) => void>();
  const creators = new Set<(creator: Creator) => void>();
  const statuses = new Set<(status: Status) => void>();
  const pauses = new Set<(paused: boolean) => void>();
  const syncStates = new Set<(state: SyncState) => void>();
  const problems = new Set<(message: string) => void>();
  const shares = new Set<(state: ShareState) => void>();
  const recoveries = new Set<() => void>();

  const each = <T>(set: Set<(value: T) => void>, value: T) => {
    for (const fn of [...set]) fn(value);
  };
  const emitItem = (change: ItemChange, listId: string) => {
    for (const fn of [...(items.get(listId) ?? [])]) fn(change);
    each(allItems, change);
  };

  /** What `current` says, passed on. Replaced whole on a switch, so a late event from the old store is nobody's. */
  let unsubscribe = attach(current);
  function attach(store: SharedStore): () => void {
    const stops = [
      store.onLists((change) => each(lists, change)),
      store.onAllItems((change) => {
        const listId = change.item?.listId ?? itemLists.get(change.id);
        if (change.item) itemLists.set(change.id, change.item.listId);
        else itemLists.delete(change.id);
        if (listId !== undefined) for (const fn of [...(items.get(listId) ?? [])]) fn(change);
        each(allItems, change);
      }),
      store.onCreators((creator) => each(creators, creator)),
      store.onStatus((status) => each(statuses, status)),
      store.onPaused((paused) => each(pauses, paused)),
      store.onSyncState((state) => each(syncStates, state)),
      store.onProblem((message) => each(problems, message)),
      store.onShare((state) => each(shares, state)),
      store.onRecoveries(() => { for (const fn of [...recoveries]) fn(); }),
    ];
    return () => { for (const stop of stops) stop(); };
  }
  /** Which list each item was last in, so a removal reaches the list it left. */
  const itemLists = new Map<string, string>(current.allItems().map((row) => [row.id, row.listId]));

  function sameList(a: List | undefined, b: List | undefined): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  function sameItem(a: Item | undefined, b: Item | undefined): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  async function switchTo(next: SharedStore): Promise<void> {
    if (next === current) return;
    const previous = current;
    unsubscribe();
    current = next;
    // Leases first, so the new store refreshes the lists on screen from the
    // start, and the old one stops.
    for (const [listId, lease] of leases) {
      const release = lease.release;
      lease.release = next.watchList(listId);
      release();
    }
    unsubscribe = attach(next);

    // Everything that differs, as if it had just arrived.
    const before = { lists: new Map(previous.lists().map((row) => [row.id, row])), items: new Map(previous.allItems().map((row) => [row.id, row])) };
    for (const row of next.lists()) {
      if (!sameList(before.lists.get(row.id), row)) each(lists, { id: row.id, list: row });
      before.lists.delete(row.id);
    }
    for (const id of before.lists.keys()) each(lists, { id, list: undefined });
    for (const row of next.allItems()) {
      const old = before.items.get(row.id);
      itemLists.set(row.id, row.listId);
      if (!sameItem(old, row)) emitItem({ id: row.id, item: row }, row.listId);
      before.items.delete(row.id);
    }
    for (const [id, row] of before.items) {
      itemLists.delete(id);
      emitItem({ id, item: undefined }, row.listId);
    }
    // Each creator a row names, once, where the name is not what it was.
    const named = new Set([...next.lists(), ...next.allItems()].flatMap((row) => (row.creatorId ? [row.creatorId] : [])));
    for (const id of named) {
      const who = next.creator(id);
      if (who && JSON.stringify(who) !== JSON.stringify(previous.creator(id))) each(creators, who);
    }
    if (next.status() !== previous.status()) each(statuses, next.status());
    if (next.paused() !== previous.paused()) each(pauses, next.paused());
    each(syncStates, next.syncState());
    each(shares, next.share());

    await previous.close().catch(() => undefined);
  }

  const watch = <T>(set: Set<T>, fn: T) => {
    set.add(fn);
    return () => { set.delete(fn); };
  };

  const store: SwitchableStore = {
    current: () => current,
    switchTo,

    share: () => current.share(),
    onShare: (fn) => watch(shares, fn),
    unsent: () => current.unsent(),
    unsentChanges: () => current.unsentChanges(),
    discard: () => current.discard(),

    importList: (source, retryId) => current.importList(source, retryId),
    recoveries: () => current.recoveries(),
    onRecoveries: (fn) => watch(recoveries, fn),
    discardRecovery: (id) => current.discardRecovery(id),
    status: () => current.status(),
    paused: () => current.paused(),
    syncState: () => current.syncState(),
    lists: () => current.lists(),
    list: (id) => current.list(id),
    items: (listId) => current.items(listId),
    item: (id) => current.item(id),
    allItems: () => current.allItems(),
    creator: (id) => current.creator(id),
    onCreators: (fn) => watch(creators, fn),
    onStatus: (fn) => watch(statuses, fn),
    onPaused: (fn) => watch(pauses, fn),
    onSyncState: (fn) => watch(syncStates, fn),
    onLists: (fn) => watch(lists, fn),
    onItems(listId, fn) {
      const set = items.get(listId) ?? new Set();
      items.set(listId, set);
      set.add(fn);
      return () => { set.delete(fn); };
    },
    onAllItems: (fn) => watch(allItems, fn),
    onProblem: (fn) => watch(problems, fn),
    createList: (input) => current.createList(input),
    patchList: (id, patch) => current.patchList(id, patch),
    removeList: (id) => current.removeList(id),
    createItem: (listId, input, anchors, options) => current.createItem(listId, input, anchors, options),
    patchItem: (id, patch) => current.patchItem(id, patch),
    completeItem: (id, context) => current.completeItem(id, context),
    undoCompletion: (id) => current.undoCompletion(id),
    moveItem: (id, anchors, link) => current.moveItem(id, anchors, link),
    moveList: (id, anchors, link) => current.moveList(id, anchors, link),
    setListOrder: (listId, orderBy) => current.setListOrder(listId, orderBy),
    removeItem: (id) => current.removeItem(id),
    clearCompleted: (listId, ids) => current.clearCompleted(listId, ids),
    sync: () => current.sync(),
    resume: () => current.resume(),
    watchList(listId) {
      const lease = leases.get(listId);
      if (lease) lease.count += 1;
      else leases.set(listId, { count: 1, release: current.watchList(listId) });
      let released = false;
      return () => {
        if (released) return;
        released = true;
        const held = leases.get(listId);
        if (!held) return;
        held.count -= 1;
        if (held.count > 0) return;
        leases.delete(listId);
        held.release();
      };
    },
    refreshing: () => current.refreshing(),
    stored: () => current.stored(),
    whenStored: () => current.whenStored(),
    async close() {
      unsubscribe();
      for (const lease of leases.values()) lease.release();
      leases.clear();
      await current.close();
    },
    destroy: () => current.destroy(),
    // A shared list reads no history (g110): each of these says so, whichever scope is current.
    activityAvailable: () => false,
    onActivityAvailable: () => () => undefined,
    activity: (target) => current.activity(target),
    watchActivity: () => () => undefined,
    moreActivity: async () => undefined,
    activityDetail: () => null,
    loadActivityDetail: async () => undefined,
    onActivityDetail: () => () => undefined,
    forgetActivity: async () => undefined,
    // A shared list's page has no Lists of its own to add to or remove from (g132).
    addedLists: () => [],
    onAddedLists: () => () => undefined,
    removeAddedList: async () => undefined,
  };
  return store;
}
